import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { User } from "@/api/entities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Save, Bell, Shield, User as UserIcon, ArrowLeft, Calendar, Globe, Link2, BellRing, Loader2, Smartphone } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGoogleCalendar } from "@/hooks/useGoogleCalendar";
import { supabase } from "@/integrations/supabase/client";
import { syncUsersToSupabase } from "@/api/functions";
import { useLocaleOptional } from "@/contexts/LocaleContext";
import { SUPPORTED_LANGUAGES } from "@/locales";
import { COMMON_TIMEZONES } from "@/hooks/useUserTimezone";
import { ProfileWhatsAppConnection } from "@/components/whatsapp/ProfileWhatsAppConnection";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { toast } from "sonner";

// Position values (stored in English in DB)
const POSITION_KEYS = [
  { value: "Sales Consultant", key: "salesConsultant" },
  { value: "Sales Development", key: "salesDevelopment" },
  { value: "Customer Service", key: "customerService" },
  { value: "Customer Experience", key: "customerExperience" },
  { value: "Case Analyst", key: "caseAnalyst" },
  { value: "Case Manager", key: "caseManager" },
  { value: "Case Assistant", key: "caseAssistant" },
  { value: "Finance Coordinator", key: "financeCoordinator" },
  { value: "Finance Analyst", key: "financeAnalyst" },
  { value: "Head of Operations", key: "headOfOperations" },
  { value: "CEO", key: "ceo" },
  { value: "Operations Coordinator", key: "operationsCoordinator" },
];

export default function Profile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [appUserId, setAppUserId] = useState(null);
  const { isConnected, checkConnection, connectGoogleCalendar, disconnectGoogleCalendar, isConnecting } = useGoogleCalendar();
  const locale = useLocaleOptional();
  const { 
    isSupported: isPushSupported, 
    isSubscribed: isPushSubscribed, 
    permission: pushPermission,
    isLoading: pushLoading,
    subscribe: subscribePush,
    unsubscribe: unsubscribePush,
    sendTestNotification
  } = usePushNotifications();
  const [isSendingTestPush, setIsSendingTestPush] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    twilio_phone_number: "",
    position: "",
    notifications_enabled: true,
    email_notifications: true,
    case_updates: true,
    hearing_reminders: true
  });

  useEffect(() => {
    loadUser();

    // Listen for Google Calendar connection success
    const handleCalendarConnected = (event) => {
      if (event.data.type === 'google_calendar_connected') {
        loadUser(); // Reload to show updated connection status
      }
    };

    window.addEventListener('message', handleCalendarConnected);

    return () => {
      window.removeEventListener('message', handleCalendarConnected);
    };
  }, []);

  const loadUser = async () => {
    setIsLoading(true);
    try {
      const userData = await User.me();
      setUser(userData);
      setFormData({
        first_name: userData.first_name || "",
        last_name: userData.last_name || "",
        email: userData.email || "",
        twilio_phone_number: userData.twilio_phone_number || "",
        position: userData.position || "",
        notifications_enabled: userData.notifications_enabled ?? true,
        email_notifications: userData.email_notifications ?? true,
        case_updates: userData.case_updates ?? true,
        hearing_reminders: userData.hearing_reminders ?? true
      });

      // Get app_user_id for Google Calendar
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) {
        let { data: appUser, error: appUserErr } = await supabase
          .from('app_users')
          .select('id')
          .eq('auth_user_id', authUser.id)
          .single();

        if (!appUser) {
          console.warn('[Profile] app_user não encontrado. Tentando sincronizar usuário...');
          try {
            await syncUsersToSupabase();
            const { data: appUserAfterSync } = await supabase
              .from('app_users')
              .select('id')
              .eq('auth_user_id', authUser.id)
              .single();
            appUser = appUserAfterSync || null;
          } catch (e) {
            console.error('Erro ao sincronizar usuário com Supabase:', e);
          }
        }
        
        if (appUser?.id) {
          setAppUserId(appUser.id);
          await checkConnection(appUser.id);
        } else if (appUserErr) {
          console.error('Erro buscando app_user:', appUserErr);
        }
      }
    } catch (error) {
      console.error("Error loading user:", error);
    }
    setIsLoading(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await User.updateMyUserData(formData);
      await loadUser();
    } catch (error) {
      console.error("Error saving settings:", error);
    }
    setIsSaving(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="bg-slate-50 p-6 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/4"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 p-6 md:p-8">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="mb-6 text-slate-600 flex items-center gap-2 hover:scale-105 transition-transform duration-200"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">{t('profile.title')}</h1>
          <p className="text-slate-600">{t('profile.subtitle')}</p>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Profile Settings */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <UserIcon className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-xl">{t('profile.information')}</CardTitle>
                  <CardDescription>{t('profile.informationDesc')}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="first_name">{t('profile.firstName')}</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    placeholder={t('profile.placeholders.firstName')}
                    className="border-slate-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">{t('profile.lastName')}</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    placeholder={t('profile.placeholders.lastName')}
                    className="border-slate-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t('profile.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    disabled
                    className="border-slate-300 bg-slate-50"
                  />
                  <p className="text-xs text-slate-500">{t('profile.emailCannotChange')}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">{t('profile.phone')}</Label>
                  <Input
                    id="phone"
                    value={formData.twilio_phone_number}
                    disabled
                    placeholder={t('profile.noTwilioNumber')}
                    className="border-slate-300 bg-slate-50"
                  />
                  <p className="text-xs text-slate-500">{t('profile.twilioAssigned')}</p>
                </div>
                {/* Position field changed to a Select dropdown */}
                <div className="space-y-2">
                  <Label htmlFor="position">{t('profile.position')}</Label>
                  <Select
                    value={formData.position}
                    onValueChange={(value) => handleChange('position', value)}
                  >
                    <SelectTrigger className="border-slate-300">
                      <SelectValue placeholder={t('profile.selectPosition')} />
                    </SelectTrigger>
                    <SelectContent>
                      {POSITION_KEYS.map((pos) => (
                        <SelectItem key={pos.value} value={pos.value}>
                          {t(`profile.positions.${pos.key}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('profile.permissionLevel')}</Label>
                  <div className="px-3 py-2 bg-slate-50 border border-slate-300 rounded-md text-slate-600 capitalize">
                    {user?.role || t('profile.defaultRole')}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notification Settings */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Bell className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-xl">{t('profile.notifications')}</CardTitle>
                  <CardDescription>{t('profile.notificationsDesc')}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>{t('profile.enableNotifications')}</Label>
                    <p className="text-sm text-slate-500">{t('profile.enableNotificationsDesc')}</p>
                  </div>
                  <Switch
                    checked={formData.notifications_enabled}
                    onCheckedChange={(checked) => handleChange('notifications_enabled', checked)}
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>{t('profile.emailNotifications')}</Label>
                    <p className="text-sm text-slate-500">{t('profile.emailNotificationsDesc')}</p>
                  </div>
                  <Switch
                    checked={formData.email_notifications}
                    onCheckedChange={(checked) => handleChange('email_notifications', checked)}
                    disabled={!formData.notifications_enabled}
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>{t('profile.caseUpdates')}</Label>
                    <p className="text-sm text-slate-500">{t('profile.caseUpdatesDesc')}</p>
                  </div>
                  <Switch
                    checked={formData.case_updates}
                    onCheckedChange={(checked) => handleChange('case_updates', checked)}
                    disabled={!formData.notifications_enabled}
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>{t('profile.hearingReminders')}</Label>
                    <p className="text-sm text-slate-500">{t('profile.hearingRemindersDesc')}</p>
                  </div>
                  <Switch
                    checked={formData.hearing_reminders}
                    onCheckedChange={(checked) => handleChange('hearing_reminders', checked)}
                    disabled={!formData.notifications_enabled}
                  />
                </div>
                <Separator />
                {/* Push Notifications (PWA) - Always visible with explicit buttons for iOS */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Smartphone className="w-4 h-4 text-muted-foreground" />
                      <Label className="text-base font-medium">Push Notifications (PWA)</Label>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Receba notificações mesmo quando o navegador estiver fechado
                    </p>
                  </div>
                  
                  {/* Debug log on render */}
                  {console.log('[Profile Push Debug]', { isPushSupported, isPushSubscribed, pushPermission, pushLoading })}
                  
                  {/* Status display */}
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className={`px-2 py-1 rounded-full ${isPushSupported ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {isPushSupported ? '✓ Suportado' : '✗ Não suportado'}
                    </span>
                    <span className={`px-2 py-1 rounded-full ${
                      pushPermission === 'granted' ? 'bg-emerald-100 text-emerald-700' : 
                      pushPermission === 'denied' ? 'bg-red-100 text-red-700' : 
                      'bg-slate-100 text-slate-600'
                    }`}>
                      Permissão: {pushPermission === 'granted' ? 'Concedida' : pushPermission === 'denied' ? 'Bloqueada' : 'Pendente'}
                    </span>
                    <span className={`px-2 py-1 rounded-full ${isPushSubscribed ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                      {isPushSubscribed ? '✓ Inscrito' : '✗ Não inscrito'}
                    </span>
                  </div>

                  {/* Action buttons - explicit for better iOS compatibility */}
                  <div className="flex flex-wrap gap-2">
                    {isPushSupported && pushPermission === 'default' && (
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={async () => {
                          const permission = await Notification.requestPermission();
                          if (permission === 'granted') {
                            toast.success('Permissão concedida! Agora ative as notificações.');
                          } else if (permission === 'denied') {
                            toast.error('Permissão negada. Altere nas configurações do sistema.');
                          }
                        }}
                        disabled={pushLoading}
                      >
                        <Bell className="w-4 h-4 mr-1" />
                        Permitir Notificações
                      </Button>
                    )}
                    
                    {isPushSupported && pushPermission === 'granted' && !isPushSubscribed && (
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={async () => {
                          const success = await subscribePush();
                          if (success) {
                            toast.success('Push notifications ativadas!');
                          } else {
                            toast.error('Falha ao ativar. Tente novamente.');
                          }
                        }}
                        disabled={pushLoading}
                      >
                        {pushLoading ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <BellRing className="w-4 h-4 mr-1" />
                        )}
                        Ativar Push
                      </Button>
                    )}
                    
                    {/* Test button - shown when subscribed OR when permission granted (for debugging) */}
                    {(isPushSubscribed || pushPermission === 'granted') && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          setIsSendingTestPush(true);
                          console.log('[Push Test] Sending test notification...');
                          const result = await sendTestNotification();
                          console.log('[Push Test] Result:', result);
                          setIsSendingTestPush(false);
                          if (result.success) {
                            toast.success('Notificação de teste enviada! Verifique a Central de Notificações.');
                          } else {
                            toast.error(result.error || 'Falha ao enviar notificação');
                          }
                        }}
                        disabled={isSendingTestPush}
                      >
                        {isSendingTestPush ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <BellRing className="w-4 h-4 mr-1" />
                        )}
                        Enviar Teste
                      </Button>
                    )}
                    
                    {isPushSubscribed && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          await unsubscribePush();
                          toast.info('Push notifications desativadas');
                        }}
                        disabled={pushLoading}
                      >
                        Desativar
                      </Button>
                    )}
                  </div>

                  {/* Help messages */}
                  {!isPushSupported && (
                    <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                      ⚠️ Seu navegador não suporta push notifications. No iPhone, abra este site no Safari e adicione à Tela de Início.
                    </p>
                  )}
                  {pushPermission === 'denied' && isPushSupported && (
                    <p className="text-xs text-red-600 bg-red-50 p-2 rounded">
                      ❌ Permissão bloqueada. No iPhone: Ajustes → Safari → Notificações → {window.location.hostname}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Account Security */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Shield className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <CardTitle className="text-xl">{t('profile.security')}</CardTitle>
                  <CardDescription>{t('profile.securityDesc')}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">{t('profile.password')}</p>
                    <p className="text-sm text-slate-500">{t('profile.passwordManagedDesc')}</p>
                  </div>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    disabled
                    className="border-slate-300"
                  >
                    {t('profile.managedByGoogle')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Language & Timezone Settings */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <Globe className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <CardTitle className="text-xl">{t('profile.languageTimezone')}</CardTitle>
                  <CardDescription>{t('profile.languageTimezoneDesc')}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="language">{t('profile.language')}</Label>
                  <Select
                    value={locale?.language || 'pt-BR'}
                    onValueChange={(value) => locale?.setLanguage(value)}
                  >
                    <SelectTrigger className="border-slate-300">
                      <SelectValue placeholder={t('profile.selectLanguage')} />
                    </SelectTrigger>
                    <SelectContent>
                      {SUPPORTED_LANGUAGES.map((lang) => (
                        <SelectItem key={lang.code} value={lang.code}>
                          <span className="flex items-center gap-2">
                            <span>{lang.flag}</span>
                            <span>{lang.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">{t('profile.languageHint')}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timezone">{t('profile.timezone')}</Label>
                  <Select
                    value={locale?.userTimezone || ''}
                    onValueChange={(value) => locale?.setTimezone(value)}
                  >
                    <SelectTrigger className="border-slate-300">
                      <SelectValue placeholder={t('profile.selectTimezone')} />
                    </SelectTrigger>
                    <SelectContent>
                      {COMMON_TIMEZONES.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          <span className="flex items-center gap-2">
                            <span>{tz.label}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">{t('profile.timezoneHint')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Integrations */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Link2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-xl">{t('profile.integrations')}</CardTitle>
                  <CardDescription>{t('profile.integrationsDesc')}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {/* Google Calendar */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{t('profile.googleCalendar')}</p>
                    <p className="text-sm text-slate-500">
                      {isConnected ? `${t('profile.connected')} ✓` : t('profile.notConnected')}
                    </p>
                  </div>
                </div>
                {isConnected ? (
                  <Button 
                    type="button"
                    variant="destructive" 
                    size="sm"
                    onClick={() => appUserId && disconnectGoogleCalendar(appUserId)}
                    disabled={isConnecting}
                  >
                    {t('profile.disconnect')}
                  </Button>
                ) : (
                  <Button 
                    type="button"
                    size="sm"
                    onClick={() => appUserId && connectGoogleCalendar(appUserId)}
                    disabled={isConnecting}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {t('profile.connect')}
                  </Button>
                )}
              </div>

              {/* WhatsApp Pessoal */}
              <ProfileWhatsAppConnection />
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={loadUser}
              disabled={isSaving}
              className="border-slate-300"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? t('profile.saving') : t('profile.saveChanges')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
